/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import throttle from 'lodash/throttle';

import {
    FlexRow,
    FlexCol,
    SearchActions as GaActions,
    withLDConsumer,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';

import * as actions from '../actions/search';
import SearchResultsList from './SearchResultsList';
import SearchCriteriaInputs from './SearchCriteriaInputs';
import * as types from '../constants';

import { SearchActionBarOverlay } from './SearchActionBarOverlay';
import { showFullScreenMap } from '../utils/mapUtils';
import { processQueryParams } from '../utils/filters';

import { SearchResultsMapV2 } from '../components/SearchResultsMapV2/SearchResultsMapV2';
import { SearchResultsMapV2Modal } from '../components/SearchResultsMapV2/SearchResultsMapV2Modal';
import { MapControlProvider } from '../context/MapControlContext';

const MOBILE_WIDTH = 992;
const MOBILE_MAP_HISTORY_KEY = 'mobileMap';

class SearchResultsPage extends React.Component {
    static propTypes = {
        dispatch: PropTypes.func.isRequired,
        router: COMPAT_ROUTER_PROP_TYPES,
        searchResults: PropTypes.object,
        search: PropTypes.object,
        flags: PropTypes.object,
    };

    static displayName = 'SearchResultsPage';

    constructor(props) {
        super(props);

        const isMobile = window.innerWidth < MOBILE_WIDTH;

        this.state = {
            screenWidth: window.innerWidth,
            isMobile,
            // Returning to search on the map's entry (e.g. back from a result opened
            // from the map, or a reload) reopens the map, so the next back closes it
            isListView: !(isMobile && window.history.state?.[MOBILE_MAP_HISTORY_KEY]),
            isFiltersDialogOpen: false,
        };
        this.updateWindowDimensions = throttle(
            this.updateWindowDimensions.bind(this),
            100
        );
        this.showMapButtonRef = React.createRef();
    }

    componentDidMount() {
        document.body.classList.add('rec-no-overflow-y');
        this.updateWindowDimensions();
        window.addEventListener('resize', this.updateWindowDimensions);
        window.addEventListener('popstate', this.onPopState);
        this.props.dispatch(actions.updateSearchCriteria('isMapExpanded', false));

        const interval = setInterval(() => {
            if (document.querySelector('df-messenger')?.shadowRoot?.appendChild) {
                clearInterval(interval);
                const style = document.createElement('style');
                style.innerHTML = '.df-messenger-wrapper { bottom: 70px; }';
                style.id = 'search-page-style';
                document.querySelector('df-messenger').shadowRoot.appendChild(style);
            }
        }, 1000);

        // Before the first search, which also fetches the legacy panel's counts
        // while `filtersVisible` is on
        this.resetLegacyFiltersVisible();
        this.refresh();
    }

    componentDidUpdate() {
        const { search } = this.props;
        if (search.isMapExpanded && this.state.isMobile) {
            this.props.dispatch(actions.updateSearchCriteria('isMapExpanded', false));
            showFullScreenMap(true);
        }
        // Flags can load after mount, and the URL can turn `filtersVisible` back on
        this.resetLegacyFiltersVisible();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions);
        window.removeEventListener('popstate', this.onPopState);
        document.body.classList.remove('rec-no-overflow-y');
    }

    onApplyFilters = () => {
        setTimeout(() => {
            this.onEnteredDates();
            this.props.dispatch(actions.navigateToSearchResults());
        }, 200);
    };

    onEnteredDates() {
        const { search } = this.props;

        const isCampingSearch = this.isCampingSearch();
        if (search?.checkin_time && search?.checkout_time && !isCampingSearch) {
            const gaEvent = {
                category: 'Search',
                action: `${search.checkin_time} to ${search.checkout_time}`,
                label: 'Selected dates',
            };

            GaActions.trackRunSearch(gaEvent, () => {});
        }
    }

    refresh = () => {
        const query = processQueryParams(this.props.router.searchParams);

        const { changed } = actions.parseQueryParams(
            query,
            this.props.dispatch,
            this.props
        );
        if (
            changed ||
            !this.props.searchResults.results ||
            this.props.searchResults.results.length === 0
        ) {
            this.props.dispatch(actions.fetchSearchResults());
        }
    };

    isCampingSearch = () => {
        const inventoryTypes = this.props?.search?.fg || [];
        const isCampingSearch = inventoryTypes.includes('camping') || false;
        return isCampingSearch;
    };

    updateWindowDimensions() {
        const isMobile = window.innerWidth < MOBILE_WIDTH;

        // The map modal is mobile-only; leaving mobile with it open would strand its
        // history entry under the desktop layout, so close it the normal way
        if (this.state.isMobile && !isMobile) this.closeMap();

        this.setState({
            screenWidth: window.innerWidth,
            isMobile,
        });
    }

    /**
     * Opens or closes the filters dialog (`searchFilterModal` flag). Its trigger
     * and the dialog render in different components depending on the width.
     * @param {boolean} isFiltersDialogOpen
     */
    onFiltersDialogOpenChange = (isFiltersDialogOpen) => {
        this.setState({ isFiltersDialogOpen });
    };

    /**
     * `search.filtersVisible` is the legacy filters panel's open state. The filters
     * dialog keeps its own, so with `searchFilterModal` on nothing clears a
     * `filtersVisible` restored from the saved search session or the
     * `filters_visible` URL param, and the legacy readers keep hiding UI for a
     * panel that never renders. Turn it off whenever it shows up.
     *
     * TODO: Once the dialog is ready to ship, remove this reset and update the
     * legacy readers of `filtersVisible` to stop depending on it:
     * - SearchResultsPage: hides the mobile `SearchActionBarOverlay` (Map button)
     * - SearchResultsList: adds the `search-filters-visible` class to the results
     * - fetchSearchResults (actions/search): fetches the legacy panel's aggregations
     * - SearchResultsMapV2: collapses the expanded map
     */
    resetLegacyFiltersVisible() {
        if (this.props.flags?.searchFilterModal && this.props.search?.filtersVisible) {
            this.props.dispatch(actions.updateSearchCriteria('filtersVisible', false));
        }
    }

    // Adds a history entry so the browser's back nav button
    // closes the map instead of leaving search.
    // The entry is tagged with MOBILE_MAP_HISTORY_KEY so it can be told apart from
    // the search entry beneath it, which shares the same URL.
    openMap = () => {
        if (!this.state.isListView) return;

        // If the current entry is already tagged (e.g. search loaded on the map's entry
        // at desktop width, then shrank to mobile), reuse it so one back() closes the map
        // instead of popping onto another tagged entry that would reopen it.
        if (!window.history.state?.[MOBILE_MAP_HISTORY_KEY]) {
            window.history.pushState(
                { ...window.history.state, [MOBILE_MAP_HISTORY_KEY]: true },
                ''
            );
        }
        this.setState({ isListView: false });
    };

    closeMap = () => {
        if (this.state.isListView || this.isClosingMap) return;

        if (window.history.state?.[MOBILE_MAP_HISTORY_KEY]) {
            // Guard against repeated close events (e.g. a held Escape key) calling
            // back() more than once before popstate fires and leaving search.
            this.isClosingMap = true;
            window.history.back();
        } else {
            this.setState({ isListView: true });
        }
    };

    onPopState = () => {
        this.isClosingMap = false;

        // Navigating forward onto the map's entry reopens the map
        if (window.history.state?.[MOBILE_MAP_HISTORY_KEY]) {
            if (this.state.isMobile) this.setState({ isListView: false });
            return;
        }

        if (this.state.isListView) return;

        this.setState({ isListView: true });

        // Clicking "Search Here" could change the URL of the map's history entry
        // so we want to carry the current search over to the entry
        window.history.replaceState(
            window.history.state,
            '',
            actions.buildSearchNavUri('/search', this.props)
        );
    };

    render() {
        const isCampingSearch = this.isCampingSearch();
        return (
            <div className="search-results">
                <div id="page-body">
                    <FlexRow hasGutters={false}>
                        <FlexCol>
                            {this.state.screenWidth >= 768 && (
                                <div
                                    role="toolbar"
                                    aria-label="Search Filters"
                                    className="header-filters-container header-primary"
                                >
                                    <SearchCriteriaInputs
                                        page={types.MAIN_SEARCH_RESULTS_PAGE}
                                        onApplyFilters={this.onApplyFilters}
                                        isCampingSearch={isCampingSearch}
                                        flags={this.props.flags}
                                        isFiltersDialogOpen={
                                            this.state.isFiltersDialogOpen
                                        }
                                        onFiltersDialogOpenChange={
                                            this.onFiltersDialogOpenChange
                                        }
                                    />
                                </div>
                            )}
                        </FlexCol>
                    </FlexRow>

                    <MapControlProvider>
                        <FlexRow hasGutters={false}>
                            <SearchResultsList
                                screenWidth={this.state.screenWidth}
                                isCampingSearch={isCampingSearch}
                                flags={this.props.flags}
                                isFiltersDialogOpen={this.state.isFiltersDialogOpen}
                                onFiltersDialogOpenChange={this.onFiltersDialogOpenChange}
                            />
                            {!this.state.isMobile ? (
                                <div className="search-right-rail-wrap">
                                    <SearchResultsMapV2
                                        data={this.props.searchResults.results}
                                        isFetching={this.props.searchResults.loading}
                                    />
                                </div>
                            ) : (
                                <SearchResultsMapV2Modal
                                    isOpen={!this.state.isListView}
                                    onClose={this.closeMap}
                                    triggerRef={this.showMapButtonRef}
                                >
                                    <SearchResultsMapV2
                                        data={this.props.searchResults.results}
                                        isFetching={this.props.searchResults.loading}
                                        isMobile
                                        closeMobileExpanded={this.closeMap}
                                    />
                                </SearchResultsMapV2Modal>
                            )}

                            {this.state.isMobile &&
                            this.state.isListView &&
                            !this.props.search.filtersVisible ? (
                                <SearchActionBarOverlay
                                    onToggle={this.openMap}
                                    showMapButtonRef={this.showMapButtonRef}
                                />
                            ) : null}
                        </FlexRow>
                    </MapControlProvider>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        search: state.search,
        searchResults: state.searchResults,
    };
};

export default withCompatRouter(
    withLDConsumer()(connect(mapStateToProps)(SearchResultsPage))
);

// cSpell:ignore checkin, ticketfacility, notreservable, timedentry, recarea, vehiclepermit, venuereservations, activitypass, treepermit, giftcard
